/**
 * Georgia State-Specific Diagnostic Questions
 * All questions reference actual GA regulations, forms, and requirements
 * Regulatory Body: DCH Healthcare Facility Regulation Division (HFRD)
 * Reference: Georgia Rules 111-8-65 (Private Home Care Providers)
 * Statutory Authority: O.C.G.A. § 31-7-300 et seq.
 * 
 * CORRECTED VERSION - Key fixes applied:
 * - All citations corrected from 111-8-62 (Personal Care Homes) to 111-8-65 (Private Home Care Providers)
 * - Record retention corrected from 7 years to 5 years per 111-8-65-.09(4)(b)(1)
 * - Training requirements aligned with 111-8-65-.09(5) and (6)
 * - Background check citations aligned with OCGA 31-7-350 et seq. and Chapter 111-8-12
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const GEORGIA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'GA-A1',
    itemId: 'A1',
    stateCode: 'GA',
    title: 'Private Home Care Provider License Status',
    description: 'Georgia requires all private home care providers to be licensed by DCH under Rules 111-8-65',
    severity: 'high',
    regulationCode: '111-8-65-.05',
    regulationName: 'Licensure',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov/divisionsoffices/hfrd/facilities-provider-information/private-home-care-program',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Private Home Care Provider license from Georgia DCH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 111-8-65-.05(1), no private home care provider shall operate without a license or provisional license from DCH.',
      },
      {
        id: 'q2',
        question: 'What is your private home care provider license expiration date?',
        inputType: 'date',
        helpText: 'Found on your license certificate. Licenses are renewed periodically (typically every 2 years).',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Georgia DCH Private Home Care Provider license is current and active per 111-8-65-.05.',
      atRisk: 'Your license expires within 90 days. Submit your renewal application to DCH immediately per 111-8-65-.06.',
      nonCompliant: 'Operating a private home care provider in Georgia without a valid DCH license violates O.C.G.A. § 31-7-300 et seq. and can result in civil penalties per 111-8-65-.13. Contact DCH at (404) 657-5700.',
      unknown: 'Verify your license status with Georgia DCH at (404) 657-5700.',
    },
    stateNotes: [
      'Licenses renewed periodically (typically every 2 years)',
      'Owner background check required per 111-8-65-.05(1)(b)',
      'Fingerprint-based GCIC and FBI checks required per OCGA 31-7-350',
      'Application fee and license fee required per 111-8-65-.06',
    ],
  },

  {
    id: 'GA-A2',
    itemId: 'A2',
    stateCode: 'GA',
    title: 'Administrator Qualifications',
    description: 'Georgia requires a qualified administrator per 111-8-65-.09(5)(a)',
    severity: 'high',
    regulationCode: '111-8-65-.09(5)(a)',
    regulationName: 'Administrator',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator appointed by the governing body?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 111-8-65-.09(5)(a), the governing body shall appoint an administrator with full authority and responsibility for operations.',
      },
      {
        id: 'q2',
        question: 'Does your administrator meet the minimum qualifications under 111-8-65-.09(5)(a)?',
        inputType: 'checklist',
        options: [
          { value: 'no_abuse', label: 'No credible evidence of abuse, neglect, sexual assault, exploitation, or deprivation' },
          { value: 'orientation', label: 'Completed orientation and training required by rules' },
          { value: 'no_false', label: 'Has not made material false statements about qualifications' },
          { value: 'background', label: 'Satisfactory fingerprint background check (GCHEXS)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your administrator meets Georgia qualification requirements per 111-8-65-.09(5)(a).',
      atRisk: 'Your administrator may not meet all DCH requirements. Review qualifications against 111-8-65-.09(5)(a).',
      nonCompliant: 'No qualified administrator is designated. Georgia requires a qualified administrator per 111-8-65-.09(5)(a).',
      unknown: 'Verify administrator qualifications with DCH.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires satisfactory background check for administrators',
      'Rules 111-8-12 specifies GCHEXS fingerprint requirements for all owners/operators',
      'Administrator must have no credible evidence of abuse, neglect, or exploitation',
      'Orientation and training completion required per 111-8-65-.09(6)(a)',
    ],
  },

  {
    id: 'GA-A3',
    itemId: 'A3',
    stateCode: 'GA',
    title: 'Governing Body Requirements',
    description: 'Georgia requires a governing body responsible for policies and compliance per 111-8-65-.04',
    severity: 'high',
    regulationCode: '111-8-65-.04',
    regulationName: 'Governing Body',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a governing body empowered to determine all policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your governing body ensure compliance with DCH rules?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets Georgia requirements per 111-8-65-.04.',
      atRisk: 'Governing body may not be fulfilling all responsibilities. Review 111-8-65-.04 requirements.',
      nonCompliant: 'No governing body established. This violates 111-8-65-.04.',
      unknown: 'Verify governing body structure meets DCH requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 authorizes DCH to establish governing body requirements',
      'Rules 111-8-12 applies background check requirements to governing body members',
      'Governing body must appoint administrator and ensure regulatory compliance',
      'Written documentation of governing body structure and responsibilities required',
    ],
  },

  {
    id: 'GA-A4',
    itemId: 'A4',
    stateCode: 'GA',
    title: 'Written Policies and Procedures',
    description: 'Georgia requires specific written policies per 111-8-65-.09',
    severity: 'medium',
    regulationCode: '111-8-65-.09',
    regulationName: 'Policies',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Georgia-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Client rights and responsibilities (111-8-65-.12)' },
          { value: 'service_plans', label: 'Service plan development and review (111-8-65-.11)' },
          { value: 'personnel', label: 'Personnel qualifications and training (111-8-65-.09(5))' },
          { value: 'supervision', label: 'Supervision procedures (111-8-65-.10(2))' },
          { value: 'complaint', label: 'Complaint procedures (111-8-65-.12(3))' },
          { value: 'background', label: 'Background check procedures (OCGA 31-7-350)' },
          { value: 'records', label: 'Client and personnel records (111-8-65-.09(4))' },
          { value: 'confidentiality', label: 'Confidentiality and record security' },
          { value: 'contracted', label: 'Contracted services requirements (111-8-65-.09(7))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Georgia 111-8-65 requirements.',
      atRisk: 'Your policy manual is missing required sections per 111-8-65. Missing policies: {missing}.',
      nonCompliant: 'Multiple required policies are missing per 111-8-65. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against 111-8-65 requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 authorizes DCH to require written policies as condition of licensure',
      'Rules 111-8-12 specifies policy requirements for background checks',
      'Policies must be reviewed and updated annually per DCH guidelines',
      'Staff must acknowledge receipt of all policies in writing',
    ],
  },

  {
    id: 'GA-A5',
    itemId: 'A5',
    stateCode: 'GA',
    title: 'Service Agreement Requirements',
    description: 'Georgia requires written service agreements with clients per 111-8-65-.09(3)',
    severity: 'high',
    regulationCode: '111-8-65-.09(3)',
    regulationName: 'Service Agreement',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you execute a written service agreement with EVERY client before services begin?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements does your service agreement include?',
        inputType: 'checklist',
        options: [
          { value: 'name', label: 'Client name and address' },
          { value: 'date', label: 'Date of referral' },
          { value: 'needs', label: 'Description of services needed' },
          { value: 'services', label: 'Description of services to be provided with frequency and duration' },
          { value: 'charges', label: 'Charges for services and billing/payment mechanisms' },
          { value: 'rights', label: 'Acknowledgment of client rights receipt (111-8-65-.12)' },
          { value: 'phone', label: 'Provider phone number for questions/complaints' },
          { value: 'dch_phone', label: 'DCH licensing authority phone number' },
          { value: 'signature', label: 'Client/responsible party signature' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service agreements meet Georgia requirements per 111-8-65-.09(3).',
      atRisk: 'Service agreements may be missing required elements. Review against 111-8-65-.09(3).',
      nonCompliant: 'Service agreements do not meet requirements or are not being used. This violates 111-8-65-.09(3).',
      unknown: 'Review service agreement requirements under 111-8-65-.09(3).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires written agreements before services begin',
      'Rules 111-8-12 reinforces consumer protection through disclosure requirements',
      'Agreement must include DCH complaint phone number per 111-8-65-.09(3)',
      'Client/responsible party signature required on service agreement',
    ],
  },

  {
    id: 'GA-A6',
    itemId: 'A6',
    stateCode: 'GA',
    title: 'Client Rights Notification',
    description: 'Georgia requires written disclosure of client rights per 111-8-65-.12',
    severity: 'high',
    regulationCode: '111-8-65-.12',
    regulationName: 'Client Rights',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written client rights notification to ALL clients before services begin?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which of the following rights are included in your client rights notification?',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to be treated with dignity and respect' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'participate', label: 'Right to participate in service planning' },
          { value: 'refuse', label: 'Right to refuse services' },
          { value: 'grievance', label: 'Right to file complaints with DCH' },
          { value: 'abuse_free', label: 'Right to be free from abuse, neglect, and exploitation' },
          { value: 'info', label: 'Right to information about services and costs' },
          { value: 'dch_contact', label: 'DCH complaint information included' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights notification meets Georgia 111-8-65-.12 requirements.',
      atRisk: 'Client rights notification is incomplete. Add missing elements per 111-8-65-.12.',
      nonCompliant: 'Failure to provide client rights notification violates 111-8-65-.12. Develop and implement client rights disclosure immediately.',
      unknown: 'Review client rights requirements under 111-8-65-.12.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 establishes client rights as condition of licensure',
      'Rules 111-8-12 protects vulnerable adults through rights disclosures',
      'Rights must be provided in writing before or at start of services',
      'DCH contact information for complaints must be included per 111-8-65-.12',
    ],
  },

  {
    id: 'GA-A7',
    itemId: 'A7',
    stateCode: 'GA',
    title: 'Complaint Procedures',
    description: 'Georgia requires formal complaint procedures per 111-8-65-.12(3)',
    severity: 'medium',
    regulationCode: '111-8-65-.12(3)',
    regulationName: 'Complaints',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a formal written complaint procedure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which elements does your complaint procedure include?',
        inputType: 'checklist',
        options: [
          { value: 'receive', label: 'Process for receiving complaints' },
          { value: 'investigate', label: 'Investigation procedures' },
          { value: 'resolution', label: 'Resolution and follow-up process' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'dch_info', label: 'Information about filing with DCH if unresolved' },
          { value: 'retaliation', label: 'Protection from retaliation for filing complaints' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint procedures meet Georgia 111-8-65-.12(3) requirements.',
      atRisk: 'Complaint procedures are incomplete. Enhance procedures per 111-8-65-.12(3).',
      nonCompliant: 'No formal complaint procedure exists. This violates 111-8-65-.12(3). Develop written procedures immediately.',
      unknown: 'Review complaint procedure requirements under 111-8-65-.12(3).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires mechanisms for client complaints',
      'Rules 111-8-12 protects clients through formal complaint processes',
      'Clients must be informed of DCH complaint process if unresolved internally',
      'Protection from retaliation for filing complaints required',
    ],
  },

  {
    id: 'GA-A8',
    itemId: 'A8',
    stateCode: 'GA',
    title: 'Record Retention (5 Years)',
    description: 'Georgia requires 5 years record retention per 111-8-65-.09(4)(b)(1)',
    severity: 'medium',
    regulationCode: '111-8-65-.09(4)(b)(1)',
    regulationName: 'Record Retention',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client records after last service?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets GA requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Georgia 111-8-65-.09(4)(b)(1) requires retention for at least 5 years from date of last service',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Georgia\'s 5-year requirement under 111-8-65-.09(4)(b)(1).',
      atRisk: 'Georgia requires 5-year record retention per 111-8-65-.09(4)(b)(1). Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Georgia\'s 5-year requirement under 111-8-65-.09(4)(b)(1). Immediately update your retention policy.',
      unknown: 'Review record retention requirements under 111-8-65-.09(4)(b)(1).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 authorizes DCH to establish record retention requirements',
      'Rules 111-8-12 specifies 5-year retention from date of last service',
      'Client and personnel records subject to retention requirement',
      'Records must be accessible for DCH inspection during retention period',
    ],
  },

  {
    id: 'GA-A9',
    itemId: 'A9',
    stateCode: 'GA',
    title: 'Contracted Services Requirements',
    description: 'Georgia requires written agreements for contracted services per 111-8-65-.09(7)',
    severity: 'medium',
    regulationCode: '111-8-65-.09(7)',
    regulationName: 'Contractors',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency use independent contractors to provide services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all staff are employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have written agreements detailing services for all contractors?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Are contracted services supervised by a supervisor of the licensed provider per 111-8-65-.10(2)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services meet Georgia requirements per 111-8-65-.09(7).',
      atRisk: 'Contracted services documentation may be incomplete.',
      nonCompliant: 'Contracted services do not meet 111-8-65-.09(7) requirements. Written agreements and supervision are required.',
      unknown: 'Review contracted services requirements under 111-8-65-.09(7).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires oversight of all services provided under agency license',
      'Rules 111-8-12 applies background check requirements to contractors',
      'Written agreements must detail services, supervision, and qualifications',
      'Licensed provider remains responsible for contracted services per 111-8-65-.10(2)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'GA-B1',
    itemId: 'B1',
    stateCode: 'GA',
    title: 'Personnel File Requirements',
    description: 'Georgia requires specific documents in each personnel file per 111-8-65-.09(4)(c)',
    severity: 'high',
    regulationCode: '111-8-65-.09(4)(c)',
    regulationName: 'Personnel Files',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'id', label: 'Identifying information (name, address, phone, emergency contact)' },
          { value: 'history', label: '5-year employment history (or complete if less than 5 years)' },
          { value: 'credentials', label: 'Documentation of education, training, or license/certification' },
          { value: 'background', label: 'Satisfactory fingerprint background check determination (GCHEXS)' },
          { value: 'registry', label: 'Registry checks (Nurse Aide, Sex Offender, OIG Exclusion)' },
          { value: 'job_desc', label: 'Job description defining duties' },
          { value: 'training', label: 'Documentation of initial training and competency' },
          { value: 'annual_training', label: 'Annual training documentation (8 hours)' },
          { value: 'bonding', label: 'Bonding documentation (if applicable for home management services)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Georgia-required documentation per 111-8-65-.09(4)(c).',
      atRisk: 'Some personnel files are missing required documents. Review 111-8-65-.09(4)(c) requirements. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 111-8-65-.09(4)(c). DCH surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 111-8-65-.09(4)(c) requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires complete personnel documentation for all staff',
      'Rules 111-8-12 mandates GCHEXS background check documentation in files',
      '5-year employment history required per 111-8-65-.09(4)(c)',
      'DCH surveyors review random sample of personnel files during inspections',
    ],
  },

  {
    id: 'GA-B2',
    itemId: 'B2',
    stateCode: 'GA',
    title: 'Georgia Board of Nursing License Verification',
    description: 'Verification of nursing licenses through Georgia Board of Nursing',
    severity: 'high',
    regulationCode: '111-8-65-.09(5)(b)',
    regulationName: 'Nursing Personnel',
    agencyName: 'Georgia Board of Nursing',
    agencyAbbr: 'GBON',
    agencyPhone: '(478) 207-2440',
    agencyUrl: 'https://sos.ga.gov/how-to-guide/how-guide-licensees/nursing',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ nursing personnel to provide nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - no nursing services provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are ALL nursing staff licenses verified through the Georgia Board of Nursing before hire?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% verified before first shift' },
          { value: 'most', label: 'Most are verified' },
          { value: 'no', label: 'No formal verification process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 111-8-65-.09(5)(b), nursing personnel must be licensed in Georgia per professional licensing laws.',
      },
      {
        id: 'q3',
        question: 'Do you track nursing license expiration dates?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated tracking with alerts' },
          { value: 'manual', label: 'Yes, manual tracking system' },
          { value: 'no', label: 'No active tracking' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'includes', questionId: 'q3', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All nursing licenses are verified per 111-8-65-.09(5)(b) and OCGA 43-26.',
      atRisk: 'License verification may be inconsistent. Verify all nurses through the Board at (478) 207-2440.',
      nonCompliant: 'Nursing licenses are not being verified. This violates 111-8-65-.09(5)(b) and OCGA 43-26 Nursing Practice Act.',
      unknown: 'Verify nursing license procedures through Georgia Board of Nursing.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires verification of all professional licenses',
      'Rules 111-8-12 mandates current credentials for licensed personnel',
      'Georgia Board of Nursing license lookup at sos.ga.gov/licensee-resources',
      'Track license expiration dates and verify annually per DCH guidelines',
    ],
  },

  {
    id: 'GA-B3',
    itemId: 'B3',
    stateCode: 'GA',
    title: 'Personal Care Assistant (PCA) Qualifications',
    description: 'PCAs must meet training and experience requirements per 111-8-65-.09(5)(c)',
    severity: 'high',
    regulationCode: '111-8-65-.09(5)(c)',
    regulationName: 'PCA Qualifications',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ Personal Care Assistants (PCAs) to provide personal care tasks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which qualification pathway do your PCAs meet per 111-8-65-.09(5)(c)(1)?',
        inputType: 'checklist',
        options: [
          { value: 'cna', label: 'Completed nurse aide training program per 42 CFR Part 483, Subpart D' },
          { value: 'competency', label: 'Passed competency examination for nurse aides recognized by DCH' },
          { value: 'experience', label: '6 months personal care experience in past 2 years' },
          { value: '40_hours', label: 'Completed 40-hour training (20 hrs before clients, 20 hrs within 6 months)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'PCAs must meet at least ONE of these qualification pathways.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 1, result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PCAs meet Georgia qualification requirements per 111-8-65-.09(5)(c).',
      atRisk: 'Verify all PCAs meet at least one qualification pathway under 111-8-65-.09(5)(c)(1).',
      nonCompliant: 'PCAs are working without meeting qualification requirements. This violates 111-8-65-.09(5)(c).',
      unknown: 'Verify PCA qualification requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires qualified personnel for client care',
      'Rules 111-8-12 mandates background checks for all PCAs',
      'Four qualification pathways: CNA, competency exam, 6 months experience, or 40-hour training',
      'Documentation of qualifications required in personnel file per 111-8-65-.09(4)(c)',
    ],
  },

  {
    id: 'GA-B4',
    itemId: 'B4',
    stateCode: 'GA',
    title: 'Companion/Sitter Qualifications',
    description: 'Companions and sitters must meet requirements per 111-8-65-.09(5)(d)',
    severity: 'medium',
    regulationCode: '111-8-65-.09(5)(d)',
    regulationName: 'Companion/Sitter',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ companions or sitters?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do companions/sitters receive orientation before client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is competency determined before client assignments?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 111-8-65-.09(5)(d)(2), competency must be determined through orientation, training, testing, and/or observation.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Companion/sitter qualifications meet Georgia 111-8-65-.09(5)(d) requirements.',
      atRisk: 'Verify companion/sitter orientation and competency requirements.',
      nonCompliant: 'Companions/sitters are working without proper orientation or competency determination per 111-8-65-.09(5)(d).',
      unknown: 'Review companion/sitter requirements under 111-8-65-.09(5)(d).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires qualified personnel for all client services',
      'Rules 111-8-12 mandates background checks for companions/sitters',
      'Orientation must be completed before client contact',
      'Competency determined through orientation, training, testing, or observation',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'GA-C1',
    itemId: 'C1',
    stateCode: 'GA',
    title: 'Fingerprint-Based Background Checks (OCGA 31-7-350)',
    description: 'Georgia requires fingerprint-based GCIC and FBI background checks through GCHEXS',
    severity: 'high',
    regulationCode: '31-7-350',
    regulationName: 'Background Checks',
    agencyName: 'Georgia Department of Community Health - Office of Inspector General',
    agencyAbbr: 'DCH/OIG',
    agencyPhone: '(833) 463-0020',
    agencyUrl: 'https://dch.georgia.gov/office-inspector-general/georgia-criminal-history-check-system-gchexs',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct access employees have a satisfactory fingerprint background check determination through GCHEXS?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have satisfactory determination on file' },
          { value: 'most', label: 'Most have it, some pending' },
          { value: 'no', label: 'No fingerprint-based checks' },
        ],
        required: true,
        helpText: 'OCGA 31-7-350 et seq. requires fingerprint-based GCIC and FBI checks through GCHEXS for all direct access employees.',
      },
      {
        id: 'q2',
        question: 'Are employees completing fingerprinting through IdentoGO/GAPS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
      {
        id: 'q3',
        question: 'Do any employees work with clients while their background check is pending?',
        inputType: 'select',
        options: [
          { value: 'no', label: 'No - never before satisfactory determination' },
          { value: 'supervised', label: 'Yes, but under direct supervision for max 30 days' },
          { value: 'unsupervised', label: 'Yes, unsupervised' },
        ],
        helpText: 'OCGA 31-7-356 allows up to 30-day grace period under direct supervision only.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT rules evaluated FIRST - employees working unsupervised before clearance is a direct violation
      { condition: 'equals', questionId: 'q3', value: 'unsupervised', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q3', value: 'supervised' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Fingerprint background check program meets OCGA 31-7-350 et seq. requirements.',
      atRisk: 'Some employees are pending background checks. Ensure 30-day supervised grace period is followed per OCGA 31-7-356.',
      nonCompliant: 'VIOLATION: Employees working unsupervised before background check clearance OR working without proper fingerprint checks. Per OCGA 31-7-350, unsupervised client contact before clearance is a direct regulatory violation. Civil penalties up to $10,000 or $500/day apply per Chapter 111-8-12. Contact GCHEXS at (833) 463-0020.',
      unknown: 'Verify background check compliance through GCHEXS.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 et seq. mandates fingerprint-based GCIC and FBI checks',
      'Rules 111-8-12 specifies GCHEXS as required background check system',
      'IdentoGO/GAPS used for fingerprint collection and submission',
      '30-day grace period under direct supervision allowed per OCGA 31-7-356',
    ],
  },

  {
    id: 'GA-C2',
    itemId: 'C2',
    stateCode: 'GA',
    title: 'Registry Checks',
    description: 'Georgia requires verification against multiple registries per OCGA 31-7-351(14)',
    severity: 'high',
    regulationCode: '31-7-351(14)',
    regulationName: 'Registry Checks',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which registry checks do you perform for ALL employees? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nurse_aide', label: 'Georgia Nurse Aide Registry' },
          { value: 'sex_offender', label: 'Georgia Sex Offender Registry' },
          { value: 'oig', label: 'OIG List of Excluded Individuals/Entities (LEIE)' },
          { value: 'sam', label: 'SAM.gov exclusion list' },
        ],
        required: true,
        helpText: 'Per OCGA 31-7-351(14), registry check includes Nurse Aide Registry, Sex Offender Registry, and OIG Exclusion List.',
      },
      {
        id: 'q2',
        question: 'Are registry checks completed BEFORE employees have client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Registry checks meet Georgia requirements per OCGA 31-7-351(14) and Chapter 111-8-12-.04.',
      atRisk: 'Registry checks may be incomplete. Verify all three required registries are checked before client contact.',
      nonCompliant: 'Required registry checks are not being performed. This violates OCGA 31-7-351(14). Check Nurse Aide Registry, Sex Offender Registry, and OIG Exclusion List.',
      unknown: 'Verify registry check procedures.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-351(14) defines registry check requirements',
      'Rules 111-8-12-.04 specifies three required registries',
      'GA Nurse Aide Registry, Sex Offender Registry, and OIG LEIE required',
      'Registry checks must be completed before client contact',
    ],
  },

  {
    id: 'GA-C3',
    itemId: 'C3',
    stateCode: 'GA',
    title: 'Disqualifying Offenses',
    description: 'Georgia specifies disqualifying criminal offenses per OCGA 31-7-351 and 42-8-60(j)',
    severity: 'high',
    regulationCode: '31-7-351',
    regulationName: 'Disqualifying Offenses',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy defining disqualifying criminal offenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you aware which offenses are permanently disqualifying under OCGA 42-8-60(j)?',
        inputType: 'select',
        options: [
          { value: 'yes_list', label: 'Yes, we maintain a reference list' },
          { value: 'yes_general', label: 'Yes, general awareness' },
          { value: 'no', label: 'No / Not familiar' },
        ],
        helpText: 'Certain offenses under OCGA 42-8-60(j) are permanently disqualifying with no 10-year exemption.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes_list' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_general', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disqualifying offense policy meets Georgia requirements.',
      atRisk: 'Review disqualifying offense list and exemption rules. Request disqualifying crimes list from DCH.',
      nonCompliant: 'No disqualifying offense policy exists. Review OCGA 31-7-351 and 42-8-60(j) requirements immediately.',
      unknown: 'Review disqualifying offense requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-351 defines disqualifying criminal offenses',
      'Rules 111-8-12 lists specific crimes that disqualify employment',
      'OCGA 42-8-60(j) specifies permanently disqualifying offenses',
      'Some offenses may be eligible for exemption after 10 years',
    ],
  },

  {
    id: 'GA-C4',
    itemId: 'C4',
    stateCode: 'GA',
    title: 'Rap Back Program Enrollment',
    description: 'Georgia offers continuous monitoring through Rap Back fingerprint retention',
    severity: 'medium',
    regulationCode: '111-8-12',
    regulationName: 'Rap Back',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(833) 463-0020',
    agencyUrl: 'https://dch.georgia.gov/office-inspector-general/georgia-criminal-history-check-system-gchexs',
    questions: [
      {
        id: 'q1',
        question: 'Are employee fingerprints retained in the Rap Back program for continuous monitoring?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, enrolled in Rap Back' },
          { value: 'no', label: 'No, fingerprints not retained' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'With Rap Back enrollment, no additional background check is required unless fingerprints are removed. Without Rap Back, satisfactory determination is valid for 12 months.',
      },
      {
        id: 'q2',
        question: 'If not enrolled in Rap Back, how often do you renew background checks?',
        inputType: 'select',
        options: [
          { value: 'annual', label: 'Annually (within 12 months)' },
          { value: 'biennial', label: 'Every 2 years' },
          { value: 'never', label: 'Only at hire' },
          { value: 'na', label: 'N/A - enrolled in Rap Back' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'annual' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'never', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'biennial', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check monitoring meets Georgia requirements through Rap Back or annual renewal.',
      atRisk: 'Without Rap Back, satisfactory determination is only valid for 12 months. Consider more frequent renewals or Rap Back enrollment.',
      nonCompliant: 'Background check renewal is not occurring. Without Rap Back, checks must be renewed within 12 months.',
      unknown: 'Verify background check renewal procedures.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 authorizes continuous monitoring through Rap Back',
      'Rules 111-8-12 specifies 12-month validity without Rap Back enrollment',
      'Rap Back provides real-time notification of new arrests/convictions',
      'Contact GCHEXS at (833) 463-0020 for Rap Back enrollment',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'GA-D1',
    itemId: 'D1',
    stateCode: 'GA',
    title: 'Mandatory Abuse Reporting',
    description: 'Georgia mandates reporting of suspected abuse, neglect, or exploitation',
    severity: 'high',
    regulationCode: '31-7-12.2',
    regulationName: 'Abuse Reporting',
    agencyName: 'Georgia Division of Aging Services',
    agencyAbbr: 'DAS',
    agencyPhone: '1-866-552-4464',
    agencyUrl: 'https://aging.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are all staff trained on mandatory reporting requirements for suspected abuse, neglect, or exploitation?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'some', label: 'Some staff are trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to the Georgia Adult Protective Services hotline (1-866-552-4464)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Is the abuse reporting hotline number posted and readily available to staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse and neglect reporting training meets Georgia requirements.',
      atRisk: 'Not all staff are trained on mandatory reporting. Train all staff immediately.',
      nonCompliant: 'Staff are not trained on mandatory reporting requirements. Georgia Adult Protective Services: 1-866-552-4464.',
      unknown: 'Verify mandatory reporting training is in place.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires training on abuse, neglect, and exploitation reporting',
      'Rules 111-8-12 protects vulnerable adults through mandatory reporting',
      'Adult Protective Services hotline: 1-866-552-4464',
      'Failure to report suspected abuse is a criminal offense under Georgia law',
    ],
  },

  {
    id: 'GA-D2',
    itemId: 'D2',
    stateCode: 'GA',
    title: 'Client Emergency Information',
    description: 'Required emergency contact and health information in client records',
    severity: 'high',
    regulationCode: '111-8-65-.09(4)(a)',
    regulationName: 'Emergency Info',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all client records contain current emergency information?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all clients have complete emergency info' },
          { value: 'most', label: 'Most clients, but some may be missing' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What emergency information is collected per 111-8-65-.09(4)(a)?',
        inputType: 'checklist',
        options: [
          { value: 'contacts', label: 'Emergency contacts with current phone numbers' },
          { value: 'physician', label: 'Personal physician names, addresses, phone numbers' },
          { value: 'responsible', label: 'Responsible party information (if applicable)' },
          { value: 'instructions', label: 'Special instructions for emergencies' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'all',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client emergency information is complete per 111-8-65-.09(4)(a).',
      atRisk: 'Some client emergency information may be incomplete. Review all client files.',
      nonCompliant: 'Client emergency information is not being maintained. This violates 111-8-65-.09(4)(a) and creates safety risks.',
      unknown: 'Verify client emergency information requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires emergency preparedness for client safety',
      'Rules 111-8-12 mandates complete client records including emergency contacts',
      'Emergency contacts, physician info, and special instructions required',
      'Information must be accessible to staff during service delivery',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'GA-E1',
    itemId: 'E1',
    stateCode: 'GA',
    title: 'PCA Initial Training (40 Hours)',
    description: 'Georgia requires 40 hours initial training for Personal Care Assistants per 111-8-65-.09(5)(c)(1)(iv)',
    severity: 'high',
    regulationCode: '111-8-65-.09(5)(c)(1)(iv)',
    regulationName: 'PCA Training',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ PCAs who qualified through the 40-hour training pathway?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all PCAs qualified through CNA, competency exam, or prior experience' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For PCAs using the 40-hour training pathway, is training completed properly?',
        inputType: 'checklist',
        options: [
          { value: '20_before', label: '20 hours completed BEFORE serving clients' },
          { value: '20_after', label: 'Additional 20 hours completed within 6 months of training start' },
          { value: 'rn_directed', label: 'Training conducted under direction of RN or qualified health professional' },
          { value: 'documented', label: 'Training completion documented in personnel file' },
          { value: 'competency', label: 'Competency determined before client assignment' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 111-8-65-.09(5)(c)(1)(iv), 20 hours must be completed before serving clients.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PCA initial training meets Georgia 111-8-65-.09(5)(c)(1)(iv) requirements.',
      atRisk: 'PCA training may not meet all requirements. Verify 20 hours before client contact and 20 additional hours within 6 months.',
      nonCompliant: 'PCAs are providing services without proper training. 111-8-65-.09(5)(c)(1)(iv) requires 20 hours BEFORE client contact.',
      unknown: 'Review PCA training requirements under 111-8-65-.09(5)(c)(1)(iv).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires qualified and trained personnel for client care',
      'Rules 111-8-12 mandates training documentation in personnel files',
      '20 hours required before client contact, additional 20 hours within 6 months',
      'Training must be under direction of RN or qualified health professional',
    ],
  },

  {
    id: 'GA-E2',
    itemId: 'E2',
    stateCode: 'GA',
    title: 'Annual In-Service Training (8 Hours)',
    description: 'Georgia requires 8 hours of annual in-service training per 111-8-65-.09(6)(b)',
    severity: 'high',
    regulationCode: '111-8-65-.09(6)(b)',
    regulationName: 'Annual Training',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees who have completed their first year receive 8 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff complete 8+ hours annually' },
          { value: 'most', label: 'Most complete required training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
        helpText: 'Per 111-8-65-.09(6)(b), 8 hours annual training required after first year of employment.',
      },
      {
        id: 'q2',
        question: 'Is annual training related to employees\' assigned duties and responsibilities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Generic training only' },
        ],
        helpText: 'Per 111-8-65-.09(6)(b), training shall be in subjects that relate to assigned duties.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets Georgia 111-8-65-.09(6)(b) requirements (8 hours).',
      atRisk: 'Some staff may be behind on annual training. 111-8-65-.09(6)(b) requires 8 hours annually after first year.',
      nonCompliant: 'No structured annual training program exists. This violates 111-8-65-.09(6)(b). Implement 8-hour annual training immediately.',
      unknown: 'Review annual training program against 111-8-65-.09(6)(b) requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires ongoing training for staff competency',
      'Rules 111-8-12 mandates documentation of all training in personnel files',
      '8 hours annually required after first year of employment',
      'Training must relate to assigned duties and responsibilities',
    ],
  },

  {
    id: 'GA-E3',
    itemId: 'E3',
    stateCode: 'GA',
    title: 'Competency Determination',
    description: 'Staff must demonstrate competency before client assignments per 111-8-65-.09(5)',
    severity: 'high',
    regulationCode: '111-8-65-.09(5)(c)',
    regulationName: 'Competency',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is competency determined for ALL PCAs before client assignments?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, competency determined for all before any client contact' },
          { value: 'most', label: 'Most PCAs have competency determination' },
          { value: 'no', label: 'No formal competency process' },
        ],
        required: true,
        helpText: 'Per 111-8-65-.09(5)(c), no PCA shall be assigned a task for which competency has not been determined.',
      },
      {
        id: 'q2',
        question: 'For clients with special conditions, do PCAs receive additional training and competency verification?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 111-8-65-.09(5)(c), no PCA shall care for clients with special conditions unless trained and competency demonstrated.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency determination meets Georgia 111-8-65-.09(5)(c) requirements.',
      atRisk: 'Competency determination may be inconsistent. Verify all PCAs before client assignment.',
      nonCompliant: 'PCAs are assigned without competency determination. This violates 111-8-65-.09(5)(c).',
      unknown: 'Review competency requirements under 111-8-65-.09(5)(c).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires competent staff for client safety',
      'Rules 111-8-12 mandates competency documentation in personnel files',
      'No PCA shall be assigned tasks for which competency not determined',
      'Additional training required for clients with special conditions',
    ],
  },

  {
    id: 'GA-E4',
    itemId: 'E4',
    stateCode: 'GA',
    title: 'Orientation Training',
    description: 'Georgia requires orientation training per 111-8-65-.09(6)(a)',
    severity: 'high',
    regulationCode: '111-8-65-.09(6)(a)',
    regulationName: 'Orientation',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all new employees complete documented orientation before client contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete orientation before client contact' },
          { value: 'most', label: 'Most do, but some may start before completion' },
          { value: 'no', label: 'No formal orientation process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which topics are covered in your orientation program per 111-8-65-.09(6)(a)?',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'rights', label: 'Client rights' },
          { value: 'confidentiality', label: 'Confidentiality requirements' },
          { value: 'abuse', label: 'Reporting of abuse, neglect, exploitation' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'duties', label: 'Specific job duties and tasks' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'all',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets Georgia 111-8-65-.09(6)(a) requirements.',
      atRisk: 'Orientation training may not be consistently completed before client contact. Review process.',
      nonCompliant: 'No formal orientation process exists. This violates 111-8-65-.09(6)(a). Implement orientation program immediately.',
      unknown: 'Verify orientation program requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires qualified and oriented personnel',
      'Rules 111-8-12 mandates orientation before client contact',
      'Orientation must cover policies, client rights, confidentiality, abuse reporting',
      'Documentation of orientation completion required in personnel file',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'GA-F1',
    itemId: 'F1',
    stateCode: 'GA',
    title: 'Service Plan Requirements',
    description: 'Written service plan required for each client per 111-8-65-.11',
    severity: 'high',
    regulationCode: '111-8-65-.11',
    regulationName: 'Service Plans',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you develop a written service plan for EVERY client?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all clients have written service plans' },
          { value: 'most', label: 'Most clients, but some may be missing' },
          { value: 'no', label: 'No formal service plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What elements do your service plans include per 111-8-65-.11?',
        inputType: 'checklist',
        options: [
          { value: 'needs', label: 'Assessed needs of the client' },
          { value: 'services', label: 'Services to be provided' },
          { value: 'frequency', label: 'Frequency of services' },
          { value: 'goals', label: 'Goals for services' },
          { value: 'instructions', label: 'Specific instructions for providing services' },
          { value: 'review', label: 'Plan review schedule' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'all',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service plans meet Georgia 111-8-65-.11 requirements.',
      atRisk: 'Service plans may be incomplete or missing for some clients. Review against 111-8-65-.11.',
      nonCompliant: 'Services are being provided without written service plans. This violates 111-8-65-.11. Immediately create plans for all clients.',
      unknown: 'Review service plan process against 111-8-65-.11 requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires individualized care planning',
      'Rules 111-8-12 mandates service plans in client records',
      'Plans must include assessed needs, services, frequency, and goals',
      'Plans must be reviewed and updated as client needs change',
    ],
  },

  {
    id: 'GA-F2',
    itemId: 'F2',
    stateCode: 'GA',
    title: 'Supervision of Services',
    description: 'Georgia requires supervision per 111-8-65-.10(2)',
    severity: 'high',
    regulationCode: '111-8-65-.10(2)',
    regulationName: 'Supervision',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide supervision as required per 111-8-65-.10(2)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If providing nursing services, is an RN supervising those services?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, RN supervises nursing services' },
          { value: 'no', label: 'No RN supervision' },
          { value: 'na', label: 'N/A - no nursing services provided' },
        ],
        helpText: 'Per 111-8-65-.10(2)(a), nursing services must be supervised by a Georgia RN.',
      },
      {
        id: 'q3',
        question: 'If providing personal care tasks to medically frail clients, is an RN or LPN supervising?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, RN or LPN supervises' },
          { value: 'no', label: 'No nurse supervision' },
          { value: 'na', label: 'N/A - no medically frail clients' },
        ],
        helpText: 'Per 111-8-65-.10(2)(b), personal care for medically frail clients must be supervised by RN or LPN.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['yes', 'na'] },
        { condition: 'includes', questionId: 'q3', value: ['yes', 'na'] },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets Georgia 111-8-65-.10(2) requirements.',
      atRisk: 'Supervision may not meet all requirements. Review 111-8-65-.10(2).',
      nonCompliant: 'Required supervision is not provided. This violates 111-8-65-.10(2). RN must supervise nursing services.',
      unknown: 'Review supervision requirements per 111-8-65-.10(2).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires appropriate clinical oversight',
      'Rules 111-8-12 mandates supervision documentation',
      'RN must supervise nursing services per 111-8-65-.10(2)(a)',
      'RN or LPN required for medically frail clients per 111-8-65-.10(2)(b)',
    ],
  },

  {
    id: 'GA-F3',
    itemId: 'F3',
    stateCode: 'GA',
    title: 'Supervisory Visits',
    description: 'Georgia requires supervisory visits for monitoring service delivery per 111-8-65-.10(2)',
    severity: 'high',
    regulationCode: '111-8-65-.10(2)',
    regulationName: 'Supervisory Visits',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct supervisory visits to monitor service delivery?',
        inputType: 'select',
        options: [
          { value: 'scheduled', label: 'Yes, on a regular schedule' },
          { value: 'occasional', label: 'Occasionally' },
          { value: 'no', label: 'No supervisory visits' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What do supervisory visits include?',
        inputType: 'checklist',
        options: [
          { value: 'competency', label: 'Observing staff competency' },
          { value: 'satisfaction', label: 'Assessing client satisfaction' },
          { value: 'plan', label: 'Reviewing service plan implementation' },
          { value: 'needs', label: 'Identifying changing client needs' },
          { value: 'documentation', label: 'Documenting visit findings' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'scheduled',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'scheduled' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervisory visits meet Georgia requirements.',
      atRisk: 'Supervisory visits are inconsistent. Implement regular schedule.',
      nonCompliant: 'No supervisory visits conducted. This violates 111-8-65-.10(2).',
      unknown: 'Review supervisory visit requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires monitoring of service delivery',
      'Rules 111-8-12 mandates supervision of all staff providing services',
      'Visits should assess competency, satisfaction, and service plan implementation',
      'Documentation of supervisory visits required in client records',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'GA-G1',
    itemId: 'G1',
    stateCode: 'GA',
    title: 'Client Record Contents',
    description: 'Required elements in each client record per 111-8-65-.09(4)(a)',
    severity: 'high',
    regulationCode: '111-8-65-.09(4)(a)',
    regulationName: 'Client Records',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents are maintained in EVERY client record per 111-8-65-.09(4)(a)?',
        inputType: 'checklist',
        options: [
          { value: 'name', label: 'Client name and address' },
          { value: 'responsible', label: 'Responsible party name/address (if applicable)' },
          { value: 'referral', label: 'Referral source and date' },
          { value: 'agreement', label: 'Signed service agreement' },
          { value: 'plan', label: 'Service plan' },
          { value: 'physicians', label: 'Personal physician information' },
          { value: 'reports', label: 'Progress notes and communications' },
          { value: 'rights', label: 'Signed client rights acknowledgment' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records contain all required elements per 111-8-65-.09(4)(a).',
      atRisk: 'Client records are missing some required elements. Review against 111-8-65-.09(4)(a).',
      nonCompliant: 'Client records are missing critical documentation. This violates 111-8-65-.09(4)(a).',
      unknown: 'Review client record requirements under 111-8-65-.09(4)(a).',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires complete client documentation',
      'Rules 111-8-12 specifies required client record elements',
      'Records must include service agreement, rights acknowledgment, service plan',
      'DCH surveyors review random sample of client files during inspections',
    ],
  },

  {
    id: 'GA-G2',
    itemId: 'G2',
    stateCode: 'GA',
    title: 'Record Confidentiality and Security',
    description: 'Written policies for record maintenance and security per 111-8-65-.09(4)(b)',
    severity: 'high',
    regulationCode: '111-8-65-.09(4)(b)',
    regulationName: 'Confidentiality',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies for record maintenance and security per 111-8-65-.09(4)(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your policies address the following?',
        inputType: 'checklist',
        options: [
          { value: 'supervise', label: 'Who supervises maintenance of records' },
          { value: 'custody', label: 'Who has custody of records' },
          { value: 'release', label: 'To whom records may be released and for what purposes' },
          { value: 'retention', label: 'How long records will be retained (minimum 5 years)' },
          { value: 'security', label: 'Security measures for records' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record maintenance and security policies meet 111-8-65-.09(4)(b) requirements.',
      atRisk: 'Record policies may be incomplete. Review against 111-8-65-.09(4)(b).',
      nonCompliant: 'No written record policies exist. This violates 111-8-65-.09(4)(b). Develop policies immediately.',
      unknown: 'Verify record policy requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires secure maintenance of client records',
      'Rules 111-8-12 mandates confidentiality protections',
      'Policies must address custody, release, retention, and security of records',
      '5-year retention required from date of last service',
    ],
  },

  {
    id: 'GA-G3',
    itemId: 'G3',
    stateCode: 'GA',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '111-8-65-.09(4)',
    regulationName: 'Document Tracking',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Private home care provider license' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'background', label: 'Background check renewals (if not in Rap Back)' },
          { value: 'nursing_licenses', label: 'Professional nursing licenses' },
          { value: 'training', label: 'Annual training due dates (8 hours)' },
          { value: 'cpr', label: 'CPR/First Aid certifications (if applicable)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 requires current licenses and certifications',
      'Rules 111-8-12 mandates current background check documentation',
      'Track agency license, professional licenses, insurance, and training due dates',
      'DCH surveyors verify document currency during inspections',
    ],
  },

  {
    id: 'GA-G4',
    itemId: 'G4',
    stateCode: 'GA',
    title: 'Inspection Readiness',
    description: 'Readiness for DCH inspections per 111-8-65-.08',
    severity: 'medium',
    regulationCode: '111-8-65-.08',
    regulationName: 'Inspections',
    agencyName: 'Georgia Department of Community Health',
    agencyAbbr: 'DCH',
    agencyPhone: '(404) 657-5700',
    agencyUrl: 'https://dch.georgia.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are your client and personnel files organized and accessible for DCH inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
        helpText: 'Per 111-8-65-.08, providers shall be inspected periodically by DCH.',
      },
      {
        id: 'q2',
        question: 'Could you produce any client\'s or employee\'s complete file within 5 minutes if DCH arrived for an inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for DCH inspection per 111-8-65-.08.',
      atRisk: 'File organization needs improvement. DCH surveyors expect immediate access to complete files.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against DCH survey requirements.',
    },
    stateNotes: [
      'O.C.G.A. 31-7-350 authorizes DCH to conduct periodic inspections',
      'Rules 111-8-12 requires records be accessible for DCH review',
      'Files must be retrievable within minutes during unannounced inspections',
      'Standardized file structure recommended for consistent compliance',
    ],
  },
];

export default GEORGIA_QUESTIONS;